/**
 * Anemoia cart recommendations row (custom).
 *
 * Drives <anemoia-cart-recs>, the "You may also like" row in the cart drawer
 * (snippets/anemoia-cart-recommendations.liquid): a pair of round arrows over
 * the ends of the row, and a wheel over the row sliding it sideways.
 *
 * Desktop only, on purpose. Touch already swipes the row, and the arrows would
 * cover a card on a phone-width drawer.
 *
 * The drawer re-renders its whole content on every cart change, so this element
 * has to survive both being replaced outright (a fresh instance connects) and
 * being morphed in place with a new set of cards underneath it (the observer
 * below catches that one).
 */

const DESKTOP = '(min-width: 750px)';
/** Ignore sub-pixel slack when deciding whether a row is at its end. */
const EDGE = 2;

class AnemoiaCartRecs extends HTMLElement {
  connectedCallback() {
    this.row = this.querySelector('[data-acart-recs-row]');
    if (!this.row) return;

    this.prevBtn = this.querySelector('[data-acart-recs-prev]');
    this.nextBtn = this.querySelector('[data-acart-recs-next]');
    this.desktop = window.matchMedia(DESKTOP);

    this.onPrev = () => this.slide(-1);
    this.onNext = () => this.slide(1);
    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);

    this.onScroll = () => this.update();
    this.row.addEventListener('scroll', this.onScroll, { passive: true });

    this.onWheel = (event) => this.wheel(event);
    this.row.addEventListener('wheel', this.onWheel, { passive: false });

    // A card's image landing, the drawer resizing, or the cart swapping the
    // cards out all change how far the row can scroll.
    this.onResize = () => this.update();
    window.addEventListener('resize', this.onResize, { passive: true });
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this.row, { childList: true });
    this.row.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', this.onResize, { once: true });
    });

    this.update();
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    if (this.row && this.onScroll) this.row.removeEventListener('scroll', this.onScroll);
    if (this.row && this.onWheel) this.row.removeEventListener('wheel', this.onWheel);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.observer?.disconnect();
  }

  /** How far one arrow press travels: a rowful of cards, less one kept for context. */
  page() {
    const card = this.row.querySelector('.acart-recs__card');
    if (!card) return this.row.clientWidth;
    const gap = parseFloat(getComputedStyle(this.row).columnGap) || 0;
    const step = card.getBoundingClientRect().width + gap;
    const fit = Math.max(1, Math.floor(this.row.clientWidth / step) - 1);
    return step * fit;
  }

  slide(dir) {
    this.row.scrollBy({ left: dir * this.page(), behavior: 'smooth' });
  }

  /**
   * Wheel over the row slides it. The drawer scrolls vertically behind this, so
   * the gesture is only taken while the row can still travel the way it is
   * heading — at either end it is handed straight back to the drawer, which is
   * what keeps a scroll down the drawer from stalling over this row.
   */
  wheel(event) {
    if (!this.desktop.matches) return;

    const max = this.row.scrollWidth - this.row.clientWidth;
    if (max <= EDGE) return;

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!delta) return;

    const at = this.row.scrollLeft;
    if (delta < 0 && at <= EDGE) return;
    if (delta > 0 && at >= max - EDGE) return;

    event.preventDefault();
    this.row.scrollLeft = at + delta;
  }

  /** Hide the arrow for an end the row is already at, and both if it cannot scroll. */
  update() {
    if (!this.row) return;
    const max = this.row.scrollWidth - this.row.clientWidth;
    const at = this.row.scrollLeft;
    const scrollable = max > EDGE;
    if (this.prevBtn) this.prevBtn.hidden = !scrollable || at <= EDGE;
    if (this.nextBtn) this.nextBtn.hidden = !scrollable || at >= max - EDGE;
  }
}

if (!customElements.get('anemoia-cart-recs')) {
  customElements.define('anemoia-cart-recs', AnemoiaCartRecs);
}
